import _styled, { default as _styled2, default as _styled3 } from "styled-components";
const _excluded = ["image", "renderImage", "width", "imageHeight", "badge", "selected", "defaultSelected", "onChange", "title", "subtitle", "tertiaryText", "renderIcons", "renderDetails", "renderOverlay", "renderActions", "style"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useControlled } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { Details, Overlay, Image } from './layout';
const defaultRenderImage = props => /*#__PURE__*/React.createElement(Image, props);
const defaultRenderDetails = props => /*#__PURE__*/React.createElement(Details, props);
const defaultRenderOverlay = props => /*#__PURE__*/React.createElement(Overlay, props);
/**
 * Tiles are components that display information related to a single subject or destination.
 * They should be used to represent items in grid layouts, such as files and folders on an application start page.
 */
const Tile = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      image,
      renderImage = defaultRenderImage,
      width = 178,
      imageHeight = 130,
      badge,
      selected: selectedProp,
      defaultSelected = false,
      onChange: onChangeProp,
      title,
      subtitle,
      tertiaryText,
      renderIcons,
      renderDetails = defaultRenderDetails,
      renderOverlay = defaultRenderOverlay,
      renderActions,
      style
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [selected, setSelected] = useControlled(defaultSelected !== null && defaultSelected !== void 0 ? defaultSelected : false, selectedProp);
  const onChange = event => {
    event.stopPropagation();
    setSelected(event.target.checked);
    onChangeProp && onChangeProp(event);
  };
  const borderColor = selected ? theme.tokens.colors.border.selected.value : theme.tokens.colors.border.subtle.value;
  return /*#__PURE__*/React.createElement(_StyledLabel, _extends({
    ref: ref
  }, props, {
    $_css: [_objectSpread(_objectSpread({
      position: 'relative',
      display: 'block',
      width,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.lowElevation
    }, selected && {
      boxShadow: `0 0 0 4px ${theme.colors.focusHalo}`
    }), {}, {
      'div.button': {
        display: 'none'
      },
      '&:hover,&:focus-within': {
        boxShadow: theme.halos.hover,
        'div.image': {
          borderColor: theme.tokens.colors.border.value
        },
        'div.details': {
          borderColor: theme.tokens.colors.border.value
        },
        'div.checkbox': {
          opacity: 1
        },
        'div.button': {
          display: 'block'
        }
      }
    }), style]
  }), /*#__PURE__*/React.createElement(_StyledDiv, {
    $_css2: [{
      position: 'absolute',
      width: '100%',
      justifyContent: 'space-between',
      padding: `6px 6px 0px 6px`,
      display: 'flex',
      zIndex: 1
    }]
  }, renderOverlay({
    defaultSelected,
    onChange,
    selected,
    badge
  })), /*#__PURE__*/React.createElement(_StyledDiv2, {
    className: "image",
    $_css3: [{
      overflow: 'hidden',
      height: imageHeight,
      background: theme.tokens.colors.background.neutral.inactive.value,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      border: `1px solid ${borderColor}`,
      boxSizing: 'border-box',
      borderRadius: `${theme.borderRadius.medium}px ${theme.borderRadius.medium}px ${theme.borderRadius.none} ${theme.borderRadius.none}`
    }]
  }, renderImage && renderImage({
    src: image
  })), renderDetails({
    className: 'details',
    title,
    subtitle,
    tertiaryText,
    renderIcons,
    renderActions,
    style: {
      borderRadius: '0px 0px 3px 3px',
      borderLeft: `1px solid ${borderColor}`,
      borderRight: `1px solid ${borderColor}`,
      borderBottom: `1px solid ${borderColor}`
    }
  }));
});
Tile.displayName = 'Tile';
Tile.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Tile title */
  title: PropTypes.string,
  /** Tile subtitle */
  subtitle: PropTypes.string,
  /** Tile tertiary text */
  tertiaryText: PropTypes.string,
  /** Render icons function */
  renderIcons: PropTypes.func,
  /** Render actions.  Generally used to render a three dot ActionButton */
  renderActions: PropTypes.func,
  /** URL to a tile image */
  image: PropTypes.string,
  /** The width of the Tile */
  width: PropTypes.number,
  /** The height of the Tile image*/
  imageHeight: PropTypes.number,
  /** The label content */
  badge: PropTypes.any,
  /** If Tile is selected. */
  selected: PropTypes.bool,
  /** If Tile is selected by default */
  defaultSelected: PropTypes.bool,
  /** onChange event handler. */
  onChange: PropTypes.func,
  /** onClick event handler. */
  onClick: PropTypes.func,
  /** Render custom image. */
  renderImage: PropTypes.func,
  /** Render custom tile overlay content. */
  renderOverlay: PropTypes.func,
  /** Render custom tile details content. */
  renderDetails: PropTypes.func
};
export default Object.assign(Tile, {
  Details,
  Image
});
var _StyledLabel = _styled("label")`${p => p.$_css}`;
var _StyledDiv = _styled2("div")`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  displayName: "Tile___StyledDiv2",
  componentId: "sc-1eq1qxk-0"
})(["", ""], p => p.$_css3);
//# sourceMappingURL=Tile.js.map